import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpArrowDown } from '@fortawesome/pro-regular-svg-icons'
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons'
import Popover from '@mui/material/Popover'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import { SORT_SECTIONS } from './fundingFilters'
import { useStyles } from './FundingRequestsSort.styles'

interface FundingRequestsSortProps {
  /** The committed sort param (e.g. 'amount_desc'), or undefined. */
  sort?: string
  onApply: (sort?: string) => void
  onClear: () => void
}

export const FundingRequestsSort = ({
  sort,
  onApply,
  onClear,
}: FundingRequestsSortProps) => {
  const { classes } = useStyles()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [draft, setDraft] = useState<string | undefined>(sort)

  // Re-seed the draft from the committed sort whenever the popover opens or the
  // committed value changes (e.g. cleared from the applied-filters bar).
  useEffect(() => {
    setDraft(sort)
  }, [sort, anchorEl])

  const open = Boolean(anchorEl)

  const close = () => setAnchorEl(null)

  const handleApply = () => {
    onApply(draft)
    close()
  }

  const handleClear = () => {
    setDraft(undefined)
    onClear()
    close()
  }

  return (
    <>
      <button
        type="button"
        className={classes.trigger}
        aria-label="Sort"
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <FontAwesomeIcon icon={faArrowUpArrowDown} />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.panel}>
          {SORT_SECTIONS.map(section => (
            <div className={classes.section} key={section.title}>
              <div className={classes.sectionHeader}>
                <Typography component="span" className={classes.sectionTitle}>
                  {section.title}
                </Typography>
                <FontAwesomeIcon icon={faCaretUp} className={classes.caret} />
              </div>
              {section.options.map(option => (
                <label className={classes.option} key={option.key}>
                  <Radio
                    className={classes.radio}
                    checked={draft === option.key}
                    value={option.key}
                    onChange={event => setDraft(event.target.value)}
                    disableRipple
                  />
                  <Typography component="span" className={classes.optionLabel}>
                    {option.label}
                  </Typography>
                </label>
              ))}
            </div>
          ))}
          <div className={classes.footer}>
            <button
              type="button"
              className={classes.linkButton}
              onClick={handleApply}
            >
              Apply
            </button>
            <button
              type="button"
              className={classes.linkButton}
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>
      </Popover>
    </>
  )
}

export default FundingRequestsSort
